import React, { useEffect, useMemo, useRef, useState } from "react";
import Styled from 'styled-components'
import Fonts from "baseStyles/enums/fonts";
import { Colors } from "baseStyles/enums/colors";
import { InputComponentsType, InputPropsType, InputTypes } from "./types";
import { useSuiTheme } from "baseStyles/ThemeProvider";
import PositionBox from "../Box/PositionBox";
import Text from "../Text";
import FlexBox from "../Box/FlexBox";
import { FlexProps } from "../Box/FlexBox/types";
import ExclamationSVG from './exclamation-icon.svg'

const computeValue = (value: number | string) => {
    if (typeof value === "number") {
        return `${value || 0}px`;
    }
    return value;
}


const InputBox = Styled.input<InputPropsType>`
    font-family: ${Fonts.Family.GRAPHIE};
    font-style: ${Fonts.Style.NORMAL};
    width: ${props => props.width ? computeValue(props.width) : '100%'};
    font-size: ${props => computeValue(props.fontSize) || `${Fonts.Size.S14}px`};
    font-weight: ${props => props.fontWeight || Fonts.Weight.W400};
    line-height: ${props => computeValue(props.lineHeight) || `${Fonts.Height.H20}px`};
    color: ${props => props.color || props.theme?.colorSecondary};
    background: transparent;
    border: ${props => computeValue(props.border) || `1px solid ${props.theme?.border}`};
    padding: ${props => computeValue(props.padding) || '10px'};
    box-sizing: border-box;
    border-radius: ${props => computeValue(props.borderRadius) || '800px'};
    height: ${props => props?.height ? (props.height + 'px') : 'auto'};
    text-align: ${props => props?.center ? 'center' : 'left'};
    ${props => props?.hideBorder && `
        border: none;
    `};

    :-internal-autofill-selected {
        color: ${props => props.color || props.theme?.colorSecondary} !important;
        background: transparent;
        appearance: none;
    };

    ::placeholder {
        color: ${props => props.placeholderColor || props.color || props.theme?.colorSecondary};
        opacity: ${props => props.placeholderOpacity};
    };
    ${props => props?.outline && `outline: ${props?.outline};`};
     &:focus {
        ${props => props?.focusBorder && `border: ${props?.focusBorder}`}
    };
    &:disabled {
        pointer-events: none;
        opacity: 1;
    };
    ${props => props?.backgroundColor && `background: ${props?.backgroundColor};`};
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

const Input: React.FC<InputPropsType> & InputComponentsType = ({ children, type, value = '', hasError, onFocus, onBlur, onChange, onClick, autoFocus, dataTestId, customPlaceholder, icon, errorMessage, ...props }) => {
    const { theme } = useSuiTheme();
    const [customText, setCustomText] = useState<string>(value);
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef(null);

    const handleOnChange = (event: any) => {
        setCustomText(event?.target?.value);
        onChange?.(event);
    }

    const onFocusHandler = (event: any) => {
        setIsFocused(true);
        onFocus?.(event);
    }

    const onBlurHandler = (event: any) => {
        setTimeout(() => setIsFocused(false), 150);
        onBlur?.(event);
    }

    const characterLimitReached = useMemo(() => {
        return customText?.length > props?.maxCharacters;
    }, [customText, props?.maxCharacters])

    if (customPlaceholder) {
        return (
            <PositionBox.relative width={props?.width || 'auto'}>
                {((customText?.length > 0) || isFocused) ?
                    <PositionBox.absolute
                        backgroundColor={Colors.WHITE}
                        top={-10} left={24}
                        padding={`0 4px`}
                        width={"auto"}
                    >
                        <Text.CAPTION
                            letterSpacing={0.4}
                            color={hasError ? Colors.DARK_RED_2 : (isFocused ? Colors.PURE_PURPLE : Colors.CONTENT_TEXT_TERTIARY)}
                        >
                            {props?.placeholder}
                        </Text.CAPTION>
                    </PositionBox.absolute>
                    : null}
                <InputBox
                    data-test-id={dataTestId}
                    ref={inputRef}
                    onFocus={onFocusHandler}
                    autoFocus={autoFocus}
                    onBlur={onBlurHandler}
                    value={value}
                    onChange={handleOnChange}
                    onClick={onClick}
                    type={type}
                    className={`sui-input`}
                    data-test-class="sui-input"
                    theme={theme}
                    {...props}
                    border={(characterLimitReached || hasError) ? `1px solid ${Colors.DARK_RED_2}` : ''}
                    focusBorder={(characterLimitReached || hasError) ? `1px solid ${Colors.DARK_RED_2} !important` : ''}
                    autoComplete="off"
                    placeholder={isFocused ? "" : props.placeholder}
                />
                {icon ? <PositionBox.absolute top={18} right={24}>{icon}</PositionBox.absolute> : null}
                {(props?.maxCharacters && isFocused) ? (
                    <FlexBox margin={`4px 0 0 0`} fullWidth justifyContent={characterLimitReached ? FlexProps.JustifyContent.SPACE_BETWEEN : FlexProps.JustifyContent.FLEX_END} alignItems={FlexProps.AlignItems.CENTER}>
                        {characterLimitReached ? <FlexBox alignItems={FlexProps.AlignItems.CENTER} gap={4}><ExclamationSVG /><Text.CAPTION letterSpacing={0.4} color={Colors.DARK_RED_2}>Character limit reached</Text.CAPTION></FlexBox> : null}
                        <FlexBox><Text.CAPTION letterSpacing={0.4} color={characterLimitReached ? Colors.DARK_RED_2 : Colors.CONTENT_TEXT_TERTIARY}>{customText?.length}/{props?.maxCharacters} characters</Text.CAPTION></FlexBox>
                    </FlexBox>
                ) : null}
                {(errorMessage && hasError) ? (
                    <FlexBox margin={`4px 0 0 10px`} fullWidth justifyContent={FlexProps.JustifyContent.FLEX_START} alignItems={FlexProps.AlignItems.CENTER}>
                        <FlexBox><Text.FOOTER letterSpacing={0.4} color={Colors.TEXT_ERROR}>{errorMessage}</Text.FOOTER></FlexBox>
                    </FlexBox>
                ) : null}
                {children}
            </PositionBox.relative>
        )
    }

    return <InputBox data-test-id={dataTestId} onFocus={onFocus} autoFocus={autoFocus} onBlur={onBlur} value={value} onChange={onChange} onClick={onClick} type={type} className={`sui-input`} data-test-class="sui-input" theme={theme} {...props} autoComplete="off" />
}

Input[InputTypes.TEXT] = (props) => <Input type={InputTypes.TEXT} {...props} />;
Input[InputTypes.PASSWORD] = (props) => <Input type={InputTypes.PASSWORD} {...props} />;

export default Input;